import { useApp } from "../../context/AppContext";
import type { FamilyBrowseMode } from "../../models/types";
import { Avatar } from "../shared/Avatar";
import { EncryptedPhoto } from "../shared/EncryptedPhoto";
import {
  IconBrain,
  IconCake,
  IconFeedback,
  IconGrid,
  IconHeart,
  IconMap,
  IconShare,
  IconSparkles,
  IconTree,
  IconGame,
  IconTrophy,
} from "../shared/Icons";
import { countdownLine, firstName, greeting } from "../../utils/calendar";

export function HomeView() {
  const {
    bundle,
    me,
    upcomingEvents,
    setActiveTab,
    setFamilyBrowseMode,
    setSelectedPersonId,
    setShowInvites,
    setShowFeedback,
    person,
    leaderboardByMode,
  } = useApp();

  if (!bundle) return null;

  const todayLine = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const myRank = me
    ? (leaderboardByMode.overall ?? []).find((e) => e.person_id === me.person_id)?.rank
    : null;

  const cousinCount = bundle.people.filter((p) => p.generation === 2 && !p.is_partner).length;

  const birthdays = upcomingEvents
    .filter((e) => e.kind === "birthday" || e.kind === "anniversary")
    .slice(0, 3);

  const jumpFamily = (mode: FamilyBrowseMode) => {
    setFamilyBrowseMode(mode);
    setActiveTab("family");
  };

  return (
    <div className="home-stack">
      <button
        type="button"
        className="greeting-hero"
        onClick={() => me && setSelectedPersonId(me.person_id)}
        disabled={!me}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1>
            {me ? `${greeting()}, ${firstName(me.display_name)}` : greeting()}
          </h1>
          <p className="muted" style={{ margin: "4px 0 0", fontWeight: 500 }}>
            {todayLine}
          </p>
        </div>
        {me ? <Avatar person={me} size={58} ring /> : null}
      </button>

      <section>
        <h2 className="section-label">
          <IconSparkles /> Jump in
        </h2>
        <div className="quick-grid">
          <button type="button" className="quick-tile" onClick={() => jumpFamily("tree")}>
            <span className="icon-well">
              <IconTree />
            </span>
            <strong>Family tree</strong>
            <span>See how everyone connects</span>
          </button>
          <button type="button" className="quick-tile" onClick={() => jumpFamily("map")}>
            <span className="icon-well">
              <IconMap />
            </span>
            <strong>On the map</strong>
            <span>Who lives where</span>
          </button>
          <button type="button" className="quick-tile" onClick={() => jumpFamily("directory")}>
            <span className="icon-well">
              <IconGrid />
            </span>
            <strong>Everyone</strong>
            <span>Browse every face</span>
          </button>
          <button type="button" className="quick-tile" onClick={() => setActiveTab("play")}>
            <span className="icon-well">
              <IconGame />
            </span>
            <strong>Play & learn</strong>
            <span>Games and flashcards</span>
          </button>
        </div>
      </section>

      <section className="stack">
        <button type="button" className="action-banner" onClick={() => setShowInvites(true)}>
          <span className="icon-well lg">
            <IconShare />
          </span>
          <div className="copy">
            <strong>Send the app</strong>
            <div className="sub">Text family or set up another phone</div>
          </div>
          <span className="chevron">›</span>
        </button>
        <button type="button" className="action-banner" onClick={() => setActiveTab("play")}>
          <span className="icon-well lg">
            {myRank ? <IconTrophy /> : <IconBrain />}
          </span>
          <div className="copy">
            <strong>{myRank ? `You're #${myRank} on the leaderboard` : "Test your family memory"}</strong>
            <div className="sub">{myRank ? "Keep climbing in Play" : "Flashcards, games & trivia"}</div>
          </div>
          <span className="chevron">›</span>
        </button>
        <button type="button" className="action-banner" onClick={() => setShowFeedback(true)}>
          <span className="icon-well lg">
            <IconFeedback />
          </span>
          <div className="copy">
            <strong>Share feedback</strong>
            <div className="sub">Report an issue or idea</div>
          </div>
          <span className="chevron">›</span>
        </button>
      </section>

      {birthdays.length > 0 ? (
        <section>
          <h2 className="section-label">
            <IconCake /> Coming up
          </h2>
          <div className="stack">
            {birthdays.map((event) => {
              const primaryId = event.person_ids[0];
              const p = primaryId ? person(primaryId) : undefined;
              return (
                <button
                  key={event.id}
                  type="button"
                  className="birthday-row"
                  onClick={() => primaryId && setSelectedPersonId(primaryId)}
                >
                  {p ? <Avatar person={p} size={48} ring /> : null}
                  <div style={{ flex: 1 }}>
                    <strong style={{ color: "var(--text)" }}>{event.label}</strong>
                    <div className="muted" style={{ fontSize: "0.85rem", marginTop: 2 }}>
                      {countdownLine(event.occurs_on)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {bundle.recent_events.length > 0 ? (
        <section>
          <h2 className="section-label">
            <IconHeart /> Family moments
          </h2>
          <div className="stack">
            {bundle.recent_events.slice(0, 5).map((event) => (
              <div key={event.id} className="moment-card">
                {event.photo_stem ? (
                  <EncryptedPhoto
                    stem={event.photo_stem}
                    alt={event.title}
                    className="moment-photo"
                    fit="cover"
                  />
                ) : null}
                <div style={{ padding: "14px 16px" }}>
                  <strong style={{ color: "var(--text)" }}>{event.title}</strong>
                  {event.subtitle ? (
                    <div className="muted" style={{ fontSize: "0.85rem", marginTop: 2 }}>
                      {event.subtitle}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <div className="stats-row">
        <div className="stat-pill">
          <strong>{bundle.people.length}</strong>
          <span>in the family</span>
        </div>
        <div className="stat-pill">
          <strong>{new Set(bundle.people.map((p) => p.generation)).size}</strong>
          <span>generations</span>
        </div>
        <div className="stat-pill">
          <strong>{cousinCount}</strong>
          <span>cousins</span>
        </div>
      </div>
    </div>
  );
}
